import { DataSourceSortableState, DataSourceSortDirection, ISortHandler, SortableOptions } from "client/Data/sorting";
import { Dispatch, SetStateAction, useMemo } from "react";

const DEFAULT_STATE: DataSourceSortableState<unknown> = { direction: DataSourceSortDirection.None };

export function useSortHandler<TDataItem>(
	state: DataSourceSortableState<TDataItem>,
	setState: Dispatch<SetStateAction<DataSourceSortableState<TDataItem>>>,
	disallowUnsorted?: boolean
) : ISortHandler<TDataItem>;
export function useSortHandler<TDataItem>(
	state: DataSourceSortableState<TDataItem> | undefined,
	setState: Dispatch<SetStateAction<DataSourceSortableState<TDataItem> | undefined>>,
	disallowUnsorted?: boolean
) : ISortHandler<TDataItem>;
export function useSortHandler<TDataItem>(
	state: DataSourceSortableState<TDataItem> | undefined,
	setState: Dispatch<SetStateAction<DataSourceSortableState<TDataItem> | undefined>> | Dispatch<SetStateAction<DataSourceSortableState<TDataItem>>>,
	disallowUnsorted?: boolean
) : ISortHandler<TDataItem> {
	const definedState: DataSourceSortableState<TDataItem> = state ?? DEFAULT_STATE;

	const result = useMemo(
		(): ISortHandler<TDataItem> => {
			const definedSetState: Dispatch<SetStateAction<DataSourceSortableState<TDataItem>>> = (
				valueAccessor: SetStateAction<DataSourceSortableState<TDataItem>>
			): void => {
				setState((
					prevState: DataSourceSortableState<TDataItem> | undefined
				): DataSourceSortableState<TDataItem> => {
					if (typeof valueAccessor === "function") {
						return valueAccessor(prevState ?? DEFAULT_STATE);
					}

					return valueAccessor;
				});
			};

			const handler = new SortHandler<TDataItem>(
				definedState,
				definedSetState,
				disallowUnsorted
			);

			// NOTE: Do not directly return an instance of a class to allow destruct capabilities
			return {
				setSorting: handler.setSorting.bind(handler),
				sortingFor: handler.sortingFor.bind(handler)
			};
		},
		[
			setState,
			definedState,
			disallowUnsorted
		]
	);

	return result;
}

class SortHandler<TDataItem> implements ISortHandler<TDataItem> {
	private readonly _state: DataSourceSortableState<TDataItem>;
	private readonly _setState: Dispatch<SetStateAction<DataSourceSortableState<TDataItem>>>;
	private readonly _disallowUnsorted: boolean | undefined;

	constructor(
		state: DataSourceSortableState<TDataItem>,
		setState: Dispatch<SetStateAction<DataSourceSortableState<TDataItem>>>,
		disallowUnsorted?: boolean
	) {
		this._state = state;
		this._setState = setState;
		this._disallowUnsorted = disallowUnsorted;
	}

	public sortingFor<TProperty extends PropertyPath<TDataItem>>(
		propertyName: TProperty
	): SortableOptions {
		return {
			direction: this._state.direction === DataSourceSortDirection.None
				? DataSourceSortDirection.None
				: this._state.propertyName === propertyName
					? this._state.direction
					: DataSourceSortDirection.None,
			sort: (newDirection: DataSourceSortDirection): void => {
				if (newDirection === DataSourceSortDirection.None) {
					this._setState({ direction: DataSourceSortDirection.None });
					return;
				}

				this._setState({ propertyName, direction: newDirection });
			},
			disallowUnsorted: this._disallowUnsorted
		};
	}

	public setSorting(state: DataSourceSortableState<TDataItem>): void {
		this._setState(state);
	}
}